// SOSPage — Notfall-Screen
// =========================
//
// Wave 14ce: Ersetzt den Offline-Tab im Bottom-Nav-Bar. User-Wunsch:
//
//   "der offline button unten nimmt viel platz und hat sehr wenig praktische
//    nutzung [...] dafür müsste da ein SOS button. für notfälle auf dem
//    mountainbike. wenn ich den öffne, sehe ich oben ganz gross die geo-
//    koordinaten samt der zahlen auf italienisch [...] dann natürlich die
//    112 als nummer, als next hospital das santa corona in pietra ligure
//    mit einem button dass man direkt zur notfallaufnahme navigiert [...]
//    weiter unten noch eine sektion bike defekt [...]"
//
// Vier Sektionen, von oben nach unten:
//   1. POSITION — Live-Koordinaten gross + italienisch phonetisch
//      ("44.1490" → "quattro quattro virgola uno quattro nove zero")
//   2. CALL 112 — fetter roter Button, opens tel:112
//   3. NEAREST HOSPITAL — Santa Corona Pietra Ligure, drei Nav-Buttons
//      (Apple/Google/Waze) via URL-Scheme
//   4. BIKE TROUBLE — Aktiviert session-only POI-Override "bike-only" und
//      navigiert zur Map; alle POI-Kategorien außer bike_shop sind temporär
//      versteckt bis App-Reload.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCurrentPosition } from '../lib/useCurrentPosition';
import { poiOverrideBus } from '../lib/poiOverrideBus';

// Wave 14ce: Santa Corona Pietra Ligure — Notfallaufnahme.
// Koordinaten vom User bestätigt 2026-05-13.
const SANTA_CORONA_HOSPITAL = {
  name: 'Santa Corona',
  city: 'Pietra Ligure',
  lat: 44.143363,
  lon: 8.271074,
};

/** Italienische Ziffer-Wörter 0–9 plus Dezimal-Trenner. Wave 14cf-fix21:
 *  Dezimal-Trenner ist "punto" (Punkt), nicht "virgola" (Komma) — die
 *  Koordinaten werden visuell mit `.` dargestellt (z.B. 44.143), also
 *  matched die Phonetik. Beide Keys ('.', ',') auf 'punto' damit Quellen
 *  die lokalisiert formatieren (mit `,`) trotzdem konsistent ausgesprochen
 *  werden. */
const IT_DIGITS: Record<string, string> = {
  '0': 'zero',
  '1': 'uno',
  '2': 'due',
  '3': 'tre',
  '4': 'quattro',
  '5': 'cinque',
  '6': 'sei',
  '7': 'sette',
  '8': 'otto',
  '9': 'nove',
  '.': 'punto',
  ',': 'punto',
};

/**
 * Wandelt eine Zahl ziffer-für-ziffer in italienische Wörter um.
 * Beispiel: "44.143" → "quattro quattro punto uno quattro tre"
 * Nicht-Ziffer-/Komma-Zeichen werden übersprungen (defensiv).
 */
function italianizePhonetic(numStr: string): string {
  const out: string[] = [];
  for (const ch of numStr) {
    const word = IT_DIGITS[ch];
    if (word) out.push(word);
  }
  return out.join(' ');
}

/** Formattiert einen Lat/Lon-Wert mit 5 Nachkommastellen (~1m Genauigkeit). */
function formatCoord(n: number): string {
  return n.toFixed(5);
}

export function SOSPage() {
  const navigate = useNavigate();
  const pos = useCurrentPosition();
  const [copied, setCopied] = useState<'lat' | 'lon' | 'both' | null>(null);
  // Wave 14cj: iOS-Navigation-Chooser. iOS hat keine System-API für
  // "App-Picker" (anders als Android's geo:-Scheme), also bauen wir den
  // Chooser selbst. Auf Android lassen wir den nativen Chooser unberührt.
  const [showNavChooser, setShowNavChooser] = useState(false);

  const copyText = async (text: string, which: 'lat' | 'lon' | 'both') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard verweigert (Permission, HTTP nicht-secure) → silent */
    }
  };

  const handleBikeDefekt = () => {
    // Wave 14ce: Override scharf schalten, sofort zur Map.
    poiOverrideBus.set('bike-only');
    navigate('/');
  };

  // Wave 14cj: Navigate-Button-Verhalten je Plattform.
  //
  //   - Android: `geo:LAT,LNG?q=LAT,LNG(Name)` → triggert den System-Chooser
  //     mit allen installierten Map-Apps (Google Maps, Waze, OsmAnd, etc.).
  //     Das ist der best-möglich auf Android — nicht mit unserem Sheet
  //     ersetzen, sonst würde OsmAnd/Komoot/etc. der User unsichtbar.
  //   - iOS:    Bottom-Sheet öffnen mit Apple Maps / Google Maps / Waze.
  //     iOS hat KEINE System-Chooser-API; einzige Alternative wäre nur
  //     Apple Maps zu öffnen (frühere Wave 14cf-fix21-Variante). User-Wunsch:
  //     "auf meinem iPhone habe ich drei [Nav-Apps] installiert" → wir
  //     bieten alle drei explizit an, der User pickt selbst.
  //   - Desktop: gleicher Sheet wie iOS (Universal-Links öffnen Web-Versionen).
  const handleNavigateToHospital = () => {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    if (isAndroid) {
      const { lat, lon, name } = SANTA_CORONA_HOSPITAL;
      window.location.href = `geo:${lat},${lon}?q=${lat},${lon}(${encodeURIComponent(name)})`;
      return;
    }
    // iOS / Desktop / unknown → in-app Chooser.
    setShowNavChooser(true);
  };

  const latStr = pos ? formatCoord(pos.latitude) : null;
  const lonStr = pos ? formatCoord(pos.longitude) : null;

  return (
    <div
      className="absolute inset-0 overflow-auto bg-turbo-900"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
      }}
    >
      <div className="px-4 pb-6 max-w-md mx-auto">
        <header className="mb-4 flex items-baseline gap-2">
          <span className="text-2xl">🆘</span>
          <h1 className="text-2xl font-bold text-white">SOS</h1>
          <span className="text-xs text-white/45 ml-auto">Emergency</span>
        </header>

        {/* ───── 1. POSITION ─────────────────────────────────────────── */}
        <section
          className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 mb-3"
          aria-label="Your current position"
        >
          <div className="flex items-start justify-between mb-2 gap-2">
            <div className="min-w-0">
              {/* Wave 14dv: Bilingual-Header — englisch oben für den User,
                  italienisch direkt darunter visuell hervorgehoben (selber
                  Style wie die phonetischen Zahlen unter den Koordinaten in
                  CoordLine: amber-100/85 italic). Hilft beim 112-Anruf
                  unmittelbar mit "ecco la mia posizione" zu sagen. */}
              <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-200/90">
                My position is
              </h2>
              <div className="text-sm text-amber-100/85 italic leading-snug mt-0.5">
                🇮🇹 La mia posizione è:
              </div>
            </div>
            {pos && (
              <button
                type="button"
                onClick={() =>
                  copyText(`${latStr}, ${lonStr}`, 'both')
                }
                className="text-[11px] px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-100 active:bg-amber-500/30"
              >
                {copied === 'both' ? '✓ Copied' : 'Copy both'}
              </button>
            )}
          </div>
          {!pos && (
            <p className="text-white/60 text-sm leading-relaxed py-2">
              Waiting for GPS… Make sure location is enabled and you have a
              clear view of the sky.
            </p>
          )}
          {pos && latStr && lonStr && (
            <>
              <CoordLine
                axis="lat"
                value={latStr}
                phonetic={italianizePhonetic(latStr)}
                copied={copied === 'lat'}
                onCopy={() => copyText(latStr, 'lat')}
              />
              <div className="h-px bg-amber-500/15 my-3" />
              <CoordLine
                axis="lon"
                value={lonStr}
                phonetic={italianizePhonetic(lonStr)}
                copied={copied === 'lon'}
                onCopy={() => copyText(lonStr, 'lon')}
              />
              <p className="text-[11px] text-amber-200/60 mt-3 leading-relaxed">
                Read the Italian spelling to a 112 operator if the line is
                poor or numbers get confused.
              </p>
            </>
          )}
        </section>

        {/* ───── 2. CALL 112 ─────────────────────────────────────────── */}
        {/* Wave 14cf: kompakter — User-Wunsch "viel schmaler". 5xl→3xl,
            p-5→py-3 px-4, text-xs sub-info weg (Police/Fire/Mountain) und
            stattdessen Inline neben der 112. */}
        <a
          href="tel:112"
          className="flex items-center gap-3 rounded-2xl bg-red-600 hover:bg-red-500 active:bg-red-700 transition-colors text-white py-3 px-4 mb-3 shadow-lg no-underline"
        >
          <span className="text-3xl font-black tabular-nums leading-none">
            112
          </span>
          <span className="flex-1 text-left leading-tight">
            <span className="block text-[10px] uppercase tracking-widest font-semibold opacity-85">
              Emergency call
            </span>
            <span className="block text-[11px] opacity-85">
              Police · Fire · Medical · Mountain rescue
            </span>
          </span>
          <span aria-hidden className="text-2xl opacity-80">📞</span>
        </a>

        {/* ───── 3. NEAREST HOSPITAL ─────────────────────────────────── */}
        {/* Wave 14cf: kompakter — single-row Layout, Navigate-Button rechts
            statt full-width. Block wird ~3× flacher, damit Bike-Trouble in
            den Viewport rutscht ohne Scroll. */}
        <section className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-3 mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-200/80 mb-1.5">
            Nearest hospital
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-[15px] leading-tight">
                {SANTA_CORONA_HOSPITAL.name}
              </div>
              <div className="text-white/55 text-[11px] leading-tight mt-0.5 tabular-nums">
                {SANTA_CORONA_HOSPITAL.city} ·{' '}
                {formatCoord(SANTA_CORONA_HOSPITAL.lat)},{' '}
                {formatCoord(SANTA_CORONA_HOSPITAL.lon)}
              </div>
            </div>
            {/* Wave 14cf-fix21: Single Navigate-Button mit OS-Aware-Routing.
                Android: System-Chooser via geo:-Scheme. iOS: Apple Maps
                Universal Link (kein Chooser möglich, OS-Limitation). */}
            <button
              type="button"
              onClick={handleNavigateToHospital}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-sky-500/30 hover:bg-sky-500/50 active:bg-sky-500/60 border border-sky-500/50 text-sky-50 text-sm font-semibold px-3 py-2 transition-colors"
              aria-label={`Navigate to ${SANTA_CORONA_HOSPITAL.name} hospital`}
            >
              <span aria-hidden>🧭</span>
              Navigate
            </button>
          </div>
        </section>

        {/* ───── 4. BIKE TROUBLE ─────────────────────────────────────── */}
        {/* Wave 14cf: kompakter — p-4→p-3, kürzere Helper-Text. */}
        <section className="rounded-2xl border border-turbo-500/30 bg-turbo-700/30 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70 mb-1.5">
            Bike trouble
          </div>
          <p className="text-[11px] text-white/55 mb-2 leading-snug">
            Filter the map to bike shops only (other POIs hidden until reload).
          </p>
          <button
            type="button"
            onClick={handleBikeDefekt}
            className="w-full rounded-lg bg-turbo-500 hover:bg-turbo-400 active:bg-turbo-600 text-white font-semibold py-2 px-4 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <span aria-hidden>🔧</span>
            Show bike shops on map
          </button>
        </section>
      </div>

      {/* Wave 14cj: iOS-Chooser-Bottom-Sheet. Auf Android nutzt
          handleNavigateToHospital direkt das geo:-Scheme → System-Chooser,
          das Sheet wird gar nicht geöffnet. */}
      {showNavChooser && (
        <NavChooserSheet
          dest={SANTA_CORONA_HOSPITAL}
          onClose={() => setShowNavChooser(false)}
        />
      )}
    </div>
  );
}

/**
 * Wave 14cj: Bottom-Sheet zum App-Picken auf iOS. Drei Buttons + ein
 * "Copy coords"-Fallback. Jeder App-Button öffnet einen Universal-Link;
 * wenn die App installiert ist, übernimmt sie automatisch, sonst fällt
 * iOS auf die Web-Version zurück (Safari).
 *
 * Apple Maps Universal-Link:
 *   https://maps.apple.com/?daddr=LAT,LON&dirflg=d
 *   - daddr = destination address (akzeptiert Lat/Lon)
 *   - dirflg=d = driving (es gibt auch w=walking, r=transit, c=cycling
 *     in macOS Maps, aber iOS Maps unterstützt c nicht durchgehend →
 *     d ist der safe default).
 *
 * Google Maps Universal-Link (api=1, offizieller Google-Spec):
 *   https://www.google.com/maps/dir/?api=1&destination=LAT,LON&travelmode=driving
 *
 * Waze Universal-Link:
 *   https://waze.com/ul?ll=LAT%2CLON&navigate=yes
 *
 * Hinweis: iOS Safari kann nicht prüfen ob eine App installiert ist
 * (Privacy-By-Design), also zeigen wir alle drei. Wenn die App nicht da
 * ist, landet der User auf der Web-Version. Akzeptabler Trade-off, weil
 * iOS halt keinen System-Chooser hat.
 */
function NavChooserSheet({
  dest,
  onClose,
}: {
  dest: { lat: number; lon: number; name: string };
  onClose: () => void;
}) {
  const { lat, lon, name } = dest;
  const appleUrl = `https://maps.apple.com/?daddr=${lat},${lon}&dirflg=d`;
  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=driving`;
  const wazeUrl = `https://waze.com/ul?ll=${lat}%2C${lon}&navigate=yes`;
  const coordsText = `${lat}, ${lon}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(coordsText);
    } catch {
      /* silent — wir schließen trotzdem damit der User weiß "passiert was" */
    }
    onClose();
  };

  // Portal weil das Sheet über dem Page-Container floaten muss
  // (z-Index, kein Schnitt durch das overflow-auto-Parent).
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Navigate to ${name}`}
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-md bg-turbo-900 border-t border-turbo-700/60 rounded-t-2xl shadow-2xl p-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-white">
            Open in…
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/55 text-2xl leading-none px-2 -mr-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="text-[12px] text-white/55 mb-3 leading-snug">
          Pick your navigation app. If it's not installed, you'll land on
          the web version.
        </p>
        <div className="space-y-2">
          <NavChoice
            href={appleUrl}
            onTap={onClose}
            label="Apple Maps"
            sub="Built-in on iPhone"
          />
          <NavChoice
            href={googleUrl}
            onTap={onClose}
            label="Google Maps"
            sub="Driving directions"
          />
          <NavChoice
            href={wazeUrl}
            onTap={onClose}
            label="Waze"
            sub="Community traffic"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-turbo-700/40 border border-turbo-500/30 text-white text-sm font-medium active:bg-turbo-700/60 transition-colors"
          >
            <span className="text-left">
              <span className="block">Copy coordinates</span>
              <span className="block text-[11px] text-white/50 font-normal">
                Paste into any app
              </span>
            </span>
            <span aria-hidden className="text-white/55">📋</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Ein Tap-Target im NavChooserSheet. Native <a> damit iOS den Universal-
 *  Link korrekt zur installierten App weiterleitet (window.location.href
 *  greift in iOS-Safari bei Universal-Links nicht immer zuverlässig). */
function NavChoice({
  href,
  onTap,
  label,
  sub,
}: {
  href: string;
  onTap: () => void;
  label: string;
  sub: string;
}) {
  return (
    <a
      href={href}
      onClick={onTap}
      target="_blank"
      rel="noreferrer noopener"
      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-sky-500/15 border border-sky-500/40 text-white text-sm font-medium active:bg-sky-500/30 transition-colors no-underline"
    >
      <span className="text-left">
        <span className="block">{label}</span>
        <span className="block text-[11px] text-white/55 font-normal">
          {sub}
        </span>
      </span>
      <span aria-hidden className="text-sky-300">→</span>
    </a>
  );
}

/** Eine Lat- oder Lon-Zeile mit grosser Zahl + italienischer Phonetik. */
function CoordLine({
  axis,
  value,
  phonetic,
  copied,
  onCopy,
}: {
  axis: 'lat' | 'lon';
  value: string;
  phonetic: string;
  copied: boolean;
  onCopy: () => void;
}) {
  const label = axis === 'lat' ? 'Latitude (N)' : 'Longitude (E)';
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-wider text-amber-200/60">
          {label}
        </span>
        <button
          type="button"
          onClick={onCopy}
          className="text-[11px] px-2 py-0.5 rounded text-amber-100/80 active:bg-amber-500/20"
          aria-label={`Copy ${label}`}
        >
          {copied ? '✓' : 'Copy'}
        </button>
      </div>
      <div className="text-3xl font-bold tabular-nums text-white font-mono leading-tight mt-0.5">
        {value}
      </div>
      <div className="text-sm text-amber-100/85 italic leading-snug mt-1">
        {phonetic}
      </div>
    </div>
  );
}

// Wave 14cj: NavApp-Helper entfernt — wurde von der pre-14cf-fix21
// Drei-Button-Grid genutzt, jetzt löst NavChooserSheet das Problem
// (Bottom-Sheet mit native <a target="_blank"> Universal-Links).
